import { useEffect, useRef, useState, type FormEvent, type MouseEvent as ReactMouseEvent } from 'react'
import {
  Bot,
  Check,
  ChevronDown,
  Clipboard,
  FolderOpen,
  ListFilter,
  Loader2,
  Library,
  MoreVertical,
  Music,
  Pause,
  Pencil,
  Play,
  RefreshCw,
  Search,
  Sparkles,
  TriangleAlert,
  Trash2,
  Upload,
  X
} from 'lucide-react'
import {
  getAudioImageUrl,
  resolveMediaUrl,
  type AudioUploadResult,
  type AudioGroup,
  type AudioTrack,
  type AudioTrackFilters
} from '../../api'
import type {
  AudioTrackLabelSaveInput,
  AudioTrackLabelSummary,
  AudioTrackLabelSuggestion,
  Label
} from '../../api/labels'
import type { SunoPrompt } from '../../api/suno'
import { Button, DataTableElement, DataTableSurface, EmptyState, IconButton, LabelBrowser, Pill, SortDirectionIcon, TablePagination, TableSortButton, labelBrowserClassName } from '../../components/ui'
import { AudioUploadDialog } from './AudioUploadDialog'
import { AudioTrackMobileList } from './AudioTrackMobileList'
import { formatBpm } from './audioFormatting'
import { useAudioUpload } from './useAudioUpload'
import styles from './AudioTracks.module.css'
import { labelCategoryLabels, labelCategoryOptions } from '../labels/labelDisplay'
import { labelChipClassName, labelMoreClassName, labelUiStyles } from '../labels/labelUiStyles'
import { normalizeLabelText } from '../labels/labelText'
import { formatDuration } from '../visuals/formatDuration'

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const lb = labelBrowserClassName

type AudioTracksProps = {
  filters: AudioTrackFilters
  groups: AudioGroup[]
  items: AudioTrack[]
  labels: Label[]
  loading: boolean
  loadingTrackId: string | null
  onDelete: (track: AudioTrack) => Promise<void>
  onEdit: (track: AudioTrack) => void
  onFilter: (filters: AudioTrackFilters) => Promise<void>
  onManageGroups: (track: AudioTrack) => void
  onPlayTrack: (track: AudioTrack) => void
  onReprocess: (track: AudioTrack) => Promise<void>
  onFindUploadPromptMatch: (stylePrompt: string) => Promise<SunoPrompt | null>
  onRegisterMobileUploadAction?: (action: (() => void) | null) => void
  onSuggestUploadPromptLabels: (stylePrompt: string) => Promise<AudioTrackLabelSuggestion[]>
  onApplyUploadLabels: (track: AudioTrack, labels: AudioTrackLabelSaveInput[]) => Promise<void>
  onPersistUploadGroups: (track: AudioTrack, groupIds: string[]) => Promise<AudioTrack>
  onPersistUploadPrompt: (track: AudioTrack, stylePrompt: string) => Promise<AudioTrack>
  onPersistUploadPromptMatch: (tracks: AudioTrack[], prompt: SunoPrompt) => Promise<AudioTrack[]>
  onUploadComplete: () => Promise<void>
  onUploadFile: (
    file: File,
    groupIds: string[],
    stylePrompt: string | null,
    sunoPromptId: string | null,
    onProgress: (progress: number) => void
  ) => Promise<AudioUploadResult>
  onUploadStateChange: (uploading: boolean) => void
  playingTrackId: string | null
  totalItems: number
  uploading: boolean
}

const audioTrackStatusLabels: Record<string, string> = {
  uploading: 'Загружается',
  queued: 'Ожидает обработки',
  processing: 'Обрабатывается',
  unsupported: 'Нет аудио',
  failed: 'Не удалось обработать',
  archived: 'В архиве'
}

const searchDebounceMs = 350
const mobileToolbarQuery = '(max-width: 768px)'
const audioTrackPageSizeOptions = [10, 25, 50, 100]
const sunoPromptBindingLabels: Record<NonNullable<AudioTrackFilters['sunoPromptBinding']>, string> = {
  linked: 'Linked',
  unlinked: 'Unlinked'
}
const losslessFilterLabels: Record<NonNullable<AudioTrackFilters['lossless']>, string> = {
  has_flac: 'Has FLAC',
  missing_flac: 'No FLAC'
}
const mskTimeZone = 'Europe/Moscow'
const addedDateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  timeZone: mskTimeZone,
  year: 'numeric'
})

const labelCategoryOrder: Record<AudioTrackLabelSummary['category'], number> = {
  genre: 0,
  mood: 1,
  use_case: 2,
  instrument: 3,
  texture: 4,
  rhythm: 5,
  tempo: 6,
  energy: 7,
  vocal_presence: 8,
  language: 9
}

const getMskDateKey = (date: Date): string =>
  new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: mskTimeZone,
    year: 'numeric'
  }).format(date)

const formatAddedDate = (createdAt: string): string => {
  const date = new Date(createdAt)

  if (Number.isNaN(date.getTime())) {
    return '—'
  }

  const now = new Date()
  const diffMs = Math.max(0, now.getTime() - date.getTime())
  const diffMinutes = Math.floor(diffMs / 60000)

  if (diffMinutes < 1) {
    return 'just now'
  }

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`
  }

  const diffHours = Math.floor(diffMinutes / 60)

  if (diffHours < 24) {
    return `${diffHours}h ago`
  }

  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)

  if (getMskDateKey(date) === getMskDateKey(yesterday)) {
    return 'yesterday'
  }

  return addedDateFormatter.format(date)
}

const getSortedTrackLabels = (track: AudioTrack): AudioTrackLabelSummary[] =>
  [...track.labels].sort((left, right) => {
    const categoryDiff = labelCategoryOrder[left.category] - labelCategoryOrder[right.category]

    return categoryDiff || left.name.localeCompare(right.name)
  })

const getEssentiaAnalysisLabel = (track: AudioTrack): string => {
  const summary = track.audio_analysis

  if (summary.status === 'queued') {
    return 'Essentia analysis queued'
  }

  if (summary.status === 'processing') {
    return 'Essentia analysis running'
  }

  if (summary.status === 'failed') {
    return summary.last_error ? `Essentia analysis failed: ${summary.last_error}` : 'Essentia analysis failed'
  }

  if (summary.status === 'completed') {
    if (summary.pending_suggestion_count > 0) {
      return `Essentia analysis completed with ${summary.pending_suggestion_count} pending suggestion(s)`
    }

    if (summary.rejected_suggestion_count > 0) {
      return 'Essentia analysis completed; suggestions were already rejected'
    }

    return 'Essentia analysis completed; no new labels found'
  }

  return 'Essentia analysis has not run'
}

const audioProcessingStepLabels: Record<NonNullable<AudioTrack['processing_summary']['current_step_type']>, string> = {
  inspect_audio: 'Inspecting audio',
  generate_audio_image_derivatives: 'Updating artwork',
  analyze_audio_features: 'Detecting BPM',
  analyze_audio_loudness: 'Analyzing loudness',
  analyze_audio_labels: 'Analyzing labels',
  generate_lossless_audio: 'Preparing FLAC'
}

const getProcessingLabel = (track: AudioTrack): string | null => {
  const summary = track.processing_summary

  if (summary.status === 'none' || summary.status === 'completed') {
    return null
  }

  const stepLabel = summary.current_step_type ? audioProcessingStepLabels[summary.current_step_type] : 'Processing'

  if (summary.status === 'failed') {
    return summary.current_step_error ? `${stepLabel} failed: ${summary.current_step_error}` : `${stepLabel} failed`
  }

  if (summary.current_step_status === 'queued') {
    return `${stepLabel} queued`
  }

  return stepLabel
}

const formatPlaybackBitrate = (bitrateKbps: number): string =>
  `${bitrateKbps} kbps`

type MobileFilterTab = 'groups' | 'prompts' | 'flac' | 'labels'

const mobileFilterTabs: Array<{ id: MobileFilterTab; label: string }> = [
  { id: 'groups', label: 'Groups' },
  { id: 'prompts', label: 'Prompts' },
  { id: 'flac', label: 'FLAC' },
  { id: 'labels', label: 'Labels' }
]

export const AudioTracks = (props: AudioTracksProps) => {
  const filterToolbarRef = useRef<HTMLDivElement | null>(null)
  const labelFilterPanelRef = useRef<HTMLDivElement | null>(null)
  const selectedGroup = props.groups.find((group) => group.id === props.filters.group)
  const selectedLabelIds = props.filters.labelIds ?? []
  const currentPage = props.filters.page ?? 1
  const pageSize = props.filters.pageSize ?? 25
  const selectedPromptBindingFilter = props.filters.sunoPromptBinding
  const totalPages = Math.max(1, Math.ceil(props.totalItems / pageSize))
  const firstItemNumber = props.totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const lastItemNumber = Math.min(currentPage * pageSize, props.totalItems)
  const selectedLabels = props.labels
    .filter((label) => selectedLabelIds.includes(label.id))
    .sort((left, right) => {
      const categoryDiff = labelCategoryOptions.indexOf(left.category) - labelCategoryOptions.indexOf(right.category)

      return categoryDiff || left.name.localeCompare(right.name)
    })
  const selectedLosslessFilter = props.filters.lossless
  const activeFilterCount = (props.filters.group ? 1 : 0) + selectedLabels.length + (selectedPromptBindingFilter ? 1 : 0) + (selectedLosslessFilter ? 1 : 0)
  const [labelFilterDialogOpen, setLabelFilterDialogOpen] = useState(false)
  const [groupFilterDraftId, setGroupFilterDraftId] = useState<string | undefined>(props.filters.group)
  const [labelFilterDraftIds, setLabelFilterDraftIds] = useState<string[]>(selectedLabelIds)
  const [sunoPromptBindingDraft, setSunoPromptBindingDraft] = useState<AudioTrackFilters['sunoPromptBinding']>(selectedPromptBindingFilter)
  const [losslessDraft, setLosslessDraft] = useState<AudioTrackFilters['lossless']>(selectedLosslessFilter)
  const [mobileFilterTab, setMobileFilterTab] = useState<MobileFilterTab>('groups')
  const [labelFilterSearch, setLabelFilterSearch] = useState('')
  const [labelFilterCategory, setLabelFilterCategory] = useState<Label['category'] | 'all'>('all')
  const [trackActionMenuId, setTrackActionMenuId] = useState<string | null>(null)
  const [trackActionMenuPosition, setTrackActionMenuPosition] = useState<{
    left: number
    placement: 'above' | 'below'
    top: number
  } | null>(null)
  const [searchInput, setSearchInput] = useState(props.filters.search ?? '')
  const [isMobileToolbar, setIsMobileToolbar] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(mobileToolbarQuery).matches
  )
  const upload = useAudioUpload({
    onApplyUploadLabels: props.onApplyUploadLabels,
    onFindUploadPromptMatch: props.onFindUploadPromptMatch,
    onPersistUploadGroups: props.onPersistUploadGroups,
    onPersistUploadPrompt: props.onPersistUploadPrompt,
    onPersistUploadPromptMatch: props.onPersistUploadPromptMatch,
    onRegisterMobileUploadAction: props.onRegisterMobileUploadAction,
    onSuggestUploadPromptLabels: props.onSuggestUploadPromptLabels,
    onUploadComplete: props.onUploadComplete,
    onUploadFile: props.onUploadFile,
    onUploadStateChange: props.onUploadStateChange
  })

  const copyTrackLabels = async (track: AudioTrack): Promise<void> => {
    const labels = getSortedTrackLabels(track).map((label) => label.name)

    if (labels.length === 0) {
      return
    }

    await navigator.clipboard.writeText(labels.join(', '))
  }

  useEffect(() => {
    if (!trackActionMenuId) {
      return undefined
    }

    const closeTrackOverlays = (event: PointerEvent): void => {
      const target = event.target

      if (target instanceof Element && target.closest('[data-track-action-surface]')) {
        return
      }

      setTrackActionMenuId(null)
      setTrackActionMenuPosition(null)
    }

    document.addEventListener('pointerdown', closeTrackOverlays)

    return () => {
      document.removeEventListener('pointerdown', closeTrackOverlays)
    }
  }, [trackActionMenuId])

  const closeTrackActionMenu = (): void => {
    setTrackActionMenuId(null)
    setTrackActionMenuPosition(null)
  }

  const toggleTrackActionMenu = (event: ReactMouseEvent<HTMLButtonElement>, trackId: string): void => {
    if (trackActionMenuId === trackId) {
      closeTrackActionMenu()
      return
    }

    const rect = event.currentTarget.getBoundingClientRect()
    const menuWidth = 190
    const menuMaxHeight = 240
    const gap = 6
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8))
    const opensAbove = rect.bottom + gap + menuMaxHeight > window.innerHeight && rect.top > menuMaxHeight

    setTrackActionMenuId(trackId)
    setTrackActionMenuPosition({
      left,
      placement: opensAbove ? 'above' : 'below',
      top: opensAbove ? rect.top - gap : rect.bottom + gap
    })
  }

  useEffect(() => {
    if (!trackActionMenuId || !isMobileToolbar) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isMobileToolbar, trackActionMenuId])

  useEffect(() => {
    setSearchInput(props.filters.search ?? '')
  }, [props.filters.search])

  useEffect(() => {
    const mediaQuery = window.matchMedia(mobileToolbarQuery)
    const updateToolbarMode = (): void => {
      setIsMobileToolbar(mediaQuery.matches)
    }

    updateToolbarMode()
    mediaQuery.addEventListener('change', updateToolbarMode)

    return () => {
      mediaQuery.removeEventListener('change', updateToolbarMode)
    }
  }, [])

  useEffect(() => {
    if (!labelFilterDialogOpen) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [labelFilterDialogOpen])

  useEffect(() => {
    const closeFiltersOnOutsidePointerDown = (event: PointerEvent): void => {
      const target = event.target

      if (!(target instanceof Element)) {
        return
      }

      const activeToolbarMenu = target.closest('details[data-toolbar-filter-menu]')

      filterToolbarRef.current
        ?.querySelectorAll<HTMLDetailsElement>('details[data-toolbar-filter-menu][open]')
        .forEach((menu) => {
          if (activeToolbarMenu !== menu) {
            menu.open = false
          }
        })

      if (
        labelFilterDialogOpen
        && labelFilterPanelRef.current
        && !labelFilterPanelRef.current.contains(target)
      ) {
        closeLabelFilterDialog()
      }
    }

    document.addEventListener('pointerdown', closeFiltersOnOutsidePointerDown)

    return () => {
      document.removeEventListener('pointerdown', closeFiltersOnOutsidePointerDown)
    }
  }, [labelFilterDialogOpen])

  useEffect(() => {
    if (props.totalItems > 0 && currentPage > totalPages) {
      void props.onFilter({
        ...props.filters,
        page: totalPages
      })
    }
  }, [currentPage, props.filters, props.onFilter, props.totalItems, totalPages])

  useEffect(() => {
    if (isMobileToolbar) {
      return undefined
    }

    if (searchInput === (props.filters.search ?? '')) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      void props.onFilter({
        ...props.filters,
        page: 1,
        search: searchInput.trim() ? searchInput : undefined
      })
    }, searchDebounceMs)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isMobileToolbar, props.filters, props.onFilter, searchInput])

  const updateFilter = (patch: AudioTrackFilters, resetPage = true): void => {
    const search = isMobileToolbar
      ? props.filters.search
      : searchInput.trim() ? searchInput : undefined

    void props.onFilter({
      ...props.filters,
      page: resetPage ? 1 : props.filters.page,
      search,
      ...patch
    })
  }

  const paginationSummary = props.totalItems === 0 ? 'No tracks' : `${firstItemNumber}-${lastItemNumber} of ${props.totalItems}`
  const pagination = (
    <TablePagination
      ariaLabel="Audio track pagination"
      currentPage={currentPage}
      onNext={() => updateFilter({ page: Math.min(totalPages, currentPage + 1) }, false)}
      onPrevious={() => updateFilter({ page: Math.max(1, currentPage - 1) }, false)}
      pageSize={{
        onChange: (nextPageSize) => updateFilter({ page: 1, pageSize: nextPageSize }, false),
        options: audioTrackPageSizeOptions,
        value: pageSize
      }}
      summary={paginationSummary}
      totalPages={totalPages}
    />
  )

  const submitSearch = (event?: FormEvent<HTMLFormElement>): void => {
    event?.preventDefault()

    void props.onFilter({
      ...props.filters,
      page: 1,
      search: searchInput.trim() ? searchInput : undefined
    })
  }

  const toggleSort = (sortBy: NonNullable<AudioTrackFilters['sortBy']>): void => {
    const currentSortBy = props.filters.sortBy ?? 'created_at'
    const currentDirection = props.filters.sortDirection ?? 'desc'
    const nextDirection = currentSortBy === sortBy && currentDirection === 'asc' ? 'desc' : 'asc'

    updateFilter({ sortBy, sortDirection: nextDirection })
  }

  const getSortLabel = (sortBy: NonNullable<AudioTrackFilters['sortBy']>): string => {
    if ((props.filters.sortBy ?? 'created_at') !== sortBy) {
      return 'Not sorted'
    }

    return (props.filters.sortDirection ?? 'desc') === 'asc' ? 'Sorted ascending' : 'Sorted descending'
  }

  const openLabelFilterDialog = (): void => {
    setGroupFilterDraftId(props.filters.group)
    setLabelFilterDraftIds(selectedLabelIds)
    setSunoPromptBindingDraft(selectedPromptBindingFilter)
    setLosslessDraft(selectedLosslessFilter)
    setMobileFilterTab('groups')
    setLabelFilterSearch('')
    setLabelFilterCategory('all')
    setLabelFilterDialogOpen(true)
  }

  const closeLabelFilterDialog = (): void => {
    setLabelFilterDialogOpen(false)
    setLabelFilterSearch('')
    setLabelFilterCategory('all')
  }

  const toggleDraftLabelFilter = (labelId: string): void => {
    setLabelFilterDraftIds((current) =>
      current.includes(labelId)
        ? current.filter((id) => id !== labelId)
        : [...current, labelId]
    )
  }

  const applyLabelFilter = (): void => {
    if (isMobileToolbar) {
      updateFilter({
        group: groupFilterDraftId,
        labelIds: labelFilterDraftIds.length > 0 ? labelFilterDraftIds : undefined,
        sunoPromptBinding: sunoPromptBindingDraft,
        lossless: losslessDraft
      })
    } else {
      updateFilter({
        labelIds: labelFilterDraftIds.length > 0 ? labelFilterDraftIds : undefined
      })
    }

    closeLabelFilterDialog()
  }

  const clearLabelFilter = (): void => {
    setLabelFilterDraftIds([])

    if (isMobileToolbar) {
      setGroupFilterDraftId(undefined)
      setSunoPromptBindingDraft(undefined)
      setLosslessDraft(undefined)
    }
  }

  const matchesLabelFilterSearch = (label: Label): boolean => {
    const normalizedSearch = normalizeLabelText(labelFilterSearch)

    return !normalizedSearch
      || normalizeLabelText(label.name).includes(normalizedSearch)
      || normalizeLabelText(label.slug).includes(normalizedSearch)
      || label.aliases.some((alias) => normalizeLabelText(alias).includes(normalizedSearch))
  }

  const draftFilterCount = (isMobileToolbar ? (groupFilterDraftId ? 1 : 0) : 0)
    + labelFilterDraftIds.length
    + (isMobileToolbar && sunoPromptBindingDraft ? 1 : 0)
    + (isMobileToolbar && losslessDraft ? 1 : 0)
  const canClearFilterDialog = isMobileToolbar
    ? Boolean(groupFilterDraftId || labelFilterDraftIds.length > 0 || sunoPromptBindingDraft || losslessDraft || props.filters.group || selectedLabelIds.length > 0 || selectedPromptBindingFilter || selectedLosslessFilter)
    : Boolean(labelFilterDraftIds.length > 0 || selectedLabelIds.length > 0)
  const filterDialogTitle = isMobileToolbar ? 'Filter tracks' : 'Filter labels'
  const filterDialogDescription = draftFilterCount > 0
    ? `${draftFilterCount} selected`
    : isMobileToolbar
      ? 'Select groups, labels, FLAC status, or prompt links to narrow the track list.'
      : 'Select labels to narrow the track list.'
  const getMobileFilterTabCount = (tab: MobileFilterTab): number => {
    switch (tab) {
      case 'groups':
        return groupFilterDraftId ? 1 : 0
      case 'prompts':
        return sunoPromptBindingDraft ? 1 : 0
      case 'flac':
        return losslessDraft ? 1 : 0
      case 'labels':
        return labelFilterDraftIds.length
    }
  }

  const renderLabelFilterControls = () => (
    <>
      <div className={lb('search-field', 'label-browser-search')}>
        <Search size={16} />
        <input
          autoFocus
          onChange={(event) => setLabelFilterSearch(event.target.value)}
          placeholder="Search labels..."
          value={labelFilterSearch}
        />
      </div>
      <div className={lb('label-browser-categories')} aria-label="Label categories">
        <button
          className={lb('label-browser-category', labelFilterCategory === 'all' && 'active')}
          onClick={() => setLabelFilterCategory('all')}
          type="button"
        >
          All
        </button>
        {labelCategoryOptions.map((category) => (
          <button
            className={lb('label-browser-category', labelFilterCategory === category && 'active')}
            key={category}
            onClick={() => setLabelFilterCategory(category)}
            type="button"
          >
            {labelCategoryLabels[category]}
          </button>
        ))}
      </div>
    </>
  )

  const renderLabelFilterList = () => (
    <div className={lb('label-browser-list')}>
      {props.labels.length === 0 && <span className="muted">Create labels first to filter tracks.</span>}
      {labelCategoryOptions.map((category) => {
        if (labelFilterCategory !== 'all' && labelFilterCategory !== category) {
          return null
        }

        const categoryLabels = props.labels.filter((label) =>
          label.status === 'active'
          && label.category === category
          && matchesLabelFilterSearch(label)
        )

        if (categoryLabels.length === 0) {
          return null
        }

        return (
          <div className={labelUiStyles.category} key={category}>
            <span className={labelUiStyles.categoryTitle}>{labelCategoryLabels[category]}</span>
            <div className={lb('label-browser-grid')}>
              {categoryLabels.map((label) => {
                const selected = labelFilterDraftIds.includes(label.id)

                return (
                  <button
                    className={lb('label-browser-item', selected && 'selected')}
                    key={label.id}
                    onClick={() => toggleDraftLabelFilter(label.id)}
                    type="button"
                  >
                    <span className="track-suggestion-check" aria-hidden="true">
                      {selected && <Check size={13} />}
                    </span>
                    <span>
                      <strong>{label.name}</strong>
                      <em>{labelCategoryLabels[label.category]}</em>
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )

  const renderLabelFilterSelection = () => (
    <>
      {renderLabelFilterControls()}
      {renderLabelFilterList()}
    </>
  )

  return (
    <>
      <div className={styles.layout}>
        <div className={['toolbar audio-toolbar', styles.desktopToolbar].join(' ')} ref={filterToolbarRef}>
          <Button className="toolbar-upload" mobileFullWidth onClick={upload.openUploadDialog} variant="primary">
            <Upload size={16} /> {props.uploading ? 'Uploading...' : 'Upload audio'}
          </Button>
          <div className="search-field audio-toolbar-desktop-search">
            <Search size={16} />
            <input
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search tracks..."
              value={searchInput}
            />
          </div>
          <details className={cx('toolbar-filter-menu')} data-toolbar-filter-menu>
            <summary>
              <Music size={15} />
              <span>{selectedGroup?.name ?? 'All groups'}</span>
              {props.filters.group && (
                <button
                  aria-label="Clear group filter"
                  className={cx('toolbar-filter-clear')}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    updateFilter({ group: undefined })
                  }}
                  title="Clear group filter"
                  type="button"
                >
                  <X size={13} />
                </button>
              )}
              <ChevronDown size={15} />
            </summary>
            <div className={cx('toolbar-menu-panel')}>
              <button
                className={cx('toolbar-menu-option', !props.filters.group && 'active')}
                onClick={() => updateFilter({ group: undefined })}
                type="button"
              >
                All groups
              </button>
              {props.groups.map((group) => (
                <button
                  className={cx('toolbar-menu-option', props.filters.group === group.id && 'active')}
                  key={group.id}
                  onClick={() => updateFilter({ group: group.id })}
                  type="button"
                >
                  <span>{group.name}</span>
                  <Pill>{group.track_count}</Pill>
                </button>
              ))}
              {props.groups.length === 0 && <span className="muted">No audio groups created.</span>}
            </div>
          </details>
          <details className={cx('toolbar-filter-menu', 'toolbar-prompt-filter')} data-toolbar-filter-menu>
            <summary>
              <Library size={15} />
              <span>{selectedPromptBindingFilter ? sunoPromptBindingLabels[selectedPromptBindingFilter] : 'All prompts'}</span>
              {selectedPromptBindingFilter && (
                <button
                  aria-label="Clear prompt link filter"
                  className={cx('toolbar-filter-clear')}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    updateFilter({ sunoPromptBinding: undefined })
                  }}
                  title="Clear prompt link filter"
                  type="button"
                >
                  <X size={13} />
                </button>
              )}
              <ChevronDown size={15} />
            </summary>
            <div className={cx('toolbar-menu-panel')}>
              <button
                className={cx('toolbar-menu-option', !selectedPromptBindingFilter && 'active')}
                onClick={() => updateFilter({ sunoPromptBinding: undefined })}
                type="button"
              >
                All prompts
              </button>
              <button
                className={cx('toolbar-menu-option', selectedPromptBindingFilter === 'linked' && 'active')}
                onClick={() => updateFilter({ sunoPromptBinding: 'linked' })}
                type="button"
              >
                Linked
              </button>
              <button
                className={cx('toolbar-menu-option', selectedPromptBindingFilter === 'unlinked' && 'active')}
                onClick={() => updateFilter({ sunoPromptBinding: 'unlinked' })}
                type="button"
              >
                Unlinked
              </button>
            </div>
          </details>
          <details className={cx('toolbar-filter-menu')} data-toolbar-filter-menu>
            <summary>
              <Sparkles size={15} />
              <span>{selectedLosslessFilter ? losslessFilterLabels[selectedLosslessFilter] : 'All FLAC'}</span>
              {selectedLosslessFilter && (
                <button
                  aria-label="Clear FLAC filter"
                  className={cx('toolbar-filter-clear')}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    updateFilter({ lossless: undefined })
                  }}
                  title="Clear FLAC filter"
                  type="button"
                >
                  <X size={13} />
                </button>
              )}
              <ChevronDown size={15} />
            </summary>
            <div className={cx('toolbar-menu-panel')}>
              <button
                className={cx('toolbar-menu-option', !selectedLosslessFilter && 'active')}
                onClick={() => updateFilter({ lossless: undefined })}
                type="button"
              >
                All FLAC
              </button>
              {(Object.entries(losslessFilterLabels) as Array<[NonNullable<AudioTrackFilters['lossless']>, string]>).map(([value, label]) => (
                <button
                  className={cx('toolbar-menu-option', selectedLosslessFilter === value && 'active')}
                  key={value}
                  onClick={() => updateFilter({ lossless: value })}
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>
          </details>
          <Button
            className={cx('toolbar-label-filter-button', selectedLabels.length > 0 && 'active')}
            mobileFullWidth
            onClick={openLabelFilterDialog}
          >
            <ListFilter size={15} />
            {selectedLabels.length > 0 ? `${selectedLabels.length} labels` : 'Labels'}
          </Button>
          {selectedLabels.length > 0 && (
            <Button
              aria-label="Clear label filters"
              className="toolbar-clear"
              mobileFullWidth
              onClick={() => updateFilter({ labelIds: undefined })}
              title="Clear label filters"
            >
              <X size={15} /> Clear labels
            </Button>
          )}
          {selectedLabels.length > 0 && (
            <div className={cx('toolbar-active-labels')} aria-label="Active label filters">
              {selectedLabels.slice(0, 3).map((label) => (
                <span className={labelChipClassName(label.category)} key={label.id}>{label.name}</span>
              ))}
              {selectedLabels.length > 3 && <span className={labelMoreClassName}>+{selectedLabels.length - 3}</span>}
            </div>
          )}
        </div>

        <AudioTrackMobileList
          activeFilterCount={activeFilterCount}
          actionMenuTrackId={trackActionMenuId}
          emptyMessage={activeFilterCount > 0 || props.filters.search
            ? 'No audio tracks match these filters.'
            : 'No audio tracks yet.'}
          items={props.items}
          loading={props.loading}
          loadingTrackId={props.loadingTrackId}
          onCloseActionMenu={closeTrackActionMenu}
          onDelete={props.onDelete}
          onEdit={props.onEdit}
          onManageGroups={props.onManageGroups}
          onOpenFilters={openLabelFilterDialog}
          onPlayTrack={props.onPlayTrack}
          onReprocess={props.onReprocess}
          onSearchInputChange={setSearchInput}
          onSearchSubmit={submitSearch}
          onToggleActionMenu={toggleTrackActionMenu}
          playingTrackId={props.playingTrackId}
          searchInput={searchInput}
        />

        <DataTableSurface className={cx(styles.tableCard, styles.desktopTable)}>
          <div className={styles.tableScroll}>
            <DataTableElement>
            <thead>
              <tr>
                <th className="col-icon"></th>
                <th>
                  <TableSortButton
                    aria-label={`Sort by artist and title. ${getSortLabel('title')}`}
                    onClick={() => toggleSort('title')}
                  >
                    <span>Title</span>
                    {(props.filters.sortBy ?? 'created_at') === 'title' && <SortDirectionIcon direction={props.filters.sortDirection ?? 'desc'} />}
                  </TableSortButton>
                </th>
                <th>Labels</th>
                <th>
                  <TableSortButton
                    aria-label={`Sort by duration. ${getSortLabel('duration')}`}
                    onClick={() => toggleSort('duration')}
                  >
                    <span>Duration</span>
                    {props.filters.sortBy === 'duration' && <SortDirectionIcon direction={props.filters.sortDirection ?? 'desc'} />}
                  </TableSortButton>
                </th>
                <th>
                  <TableSortButton
                    aria-label={`Sort by date added. ${getSortLabel('created_at')}`}
                    onClick={() => toggleSort('created_at')}
                  >
                    <span>Added</span>
                    {(props.filters.sortBy ?? 'created_at') === 'created_at' && <SortDirectionIcon direction={props.filters.sortDirection ?? 'desc'} />}
                  </TableSortButton>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {props.items.map((track) => {
                const isReady = track.status === 'ready'
                const statusLabel = audioTrackStatusLabels[track.status] ?? track.status
                const trackDisplayTitle = track.artist ? `${track.artist} - ${track.title}` : track.title
                const artworkUrl = getAudioImageUrl(track, 'thumb')
                const trackLabels = getSortedTrackLabels(track)
                const isLoadingTrack = props.loadingTrackId === track.id
                const processingLabel = getProcessingLabel(track)
                const linkedPromptTitle = track.suno_prompt_binding?.current_prompt_title ?? track.suno_prompt_binding?.prompt_snapshot.title
                const linkedPromptTitleChanged = !!(track.suno_prompt_binding && track.suno_prompt_binding.current_prompt_title && track.suno_prompt_binding.current_prompt_title !== track.suno_prompt_binding.prompt_snapshot.title)
                const hasReadyLossless = track.lossless_status === 'ready'
  const hasPlaybackMp3 = track.playback_status === 'ready' && track.playback_format === 'mp3' && !!track.file_url
  const actionMenuOpen = trackActionMenuId === track.id

                return (
                  <tr key={track.id}>
                    <td data-label="">
                      <div className="track-leading-control">
                        {isReady ? (
                          <IconButton
                            disabled={isLoadingTrack}
                            icon={isLoadingTrack ? <span className="track-status-spinner" /> : props.playingTrackId === track.id ? <Pause size={15} /> : <Play size={15} />}
                            iconSize="play"
                            label={isLoadingTrack ? `Loading ${track.title}` : `Preview ${track.title}`}
                            loading={isLoadingTrack}
                            onClick={() => props.onPlayTrack(track)}
                            title={isLoadingTrack ? 'Loading preview' : props.playingTrackId === track.id ? 'Pause' : 'Play preview'}
                          />
                        ) : (
                          <span
                            aria-label={statusLabel}
                            className={`track-status-icon track-status-icon-${track.status}`}
                            role="img"
                            title={statusLabel}
                          >
                          {track.status === 'queued' || track.status === 'processing' || track.status === 'uploading' ? (
                            <span className="track-status-spinner" />
                          ) : (
                            <TriangleAlert size={16} />
                          )}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="track-title-cell-wrapper" data-label="">
                      <div className={styles.titleCell}>
                        <span className={styles.artworkThumb}>
                          {artworkUrl ? (
                            <img alt="" src={resolveMediaUrl(artworkUrl)} />
                          ) : (
                            <Music size={15} />
                          )}
                        </span>
                        <span className={styles.titleCopy}>
                          <strong>
                            <span>{trackDisplayTitle}</span>
                            {hasReadyLossless && (
                              <span
                                aria-label="Ready FLAC source"
                                className={styles.hqMarker}
                                title="Ready FLAC source"
                              >
                                HQ
                              </span>
                            )}
                          </strong>
                          <span
                            aria-label={linkedPromptTitle ? `Linked to: ${linkedPromptTitle}` : 'No Prompt Library link'}
                            className={cx(
                              styles.promptMarker,
                              linkedPromptTitle ? styles.promptMarkerLinked : '',
                              linkedPromptTitleChanged ? styles.promptMarkerChanged : ''
                            )}
                            title={linkedPromptTitle ? `Linked to: ${linkedPromptTitle}${linkedPromptTitleChanged ? ' (title changed since binding)' : ''}` : 'No Prompt Library link'}
                          >
                            <Library size={12} aria-hidden="true" />
                            <span>{linkedPromptTitle ?? '-'}</span>
                          </span>
                          {!isReady && (
                            <span className={`track-status-text track-status-text-${track.status}`}>
                              {statusLabel}
                            </span>
                          )}
                          {processingLabel && (
                            <span className={`track-processing-text track-processing-text-${track.processing_summary.status}`}>
                              {processingLabel}
                            </span>
                          )}
                          {(track.effective_bpm !== null || (hasPlaybackMp3 && track.playback_bitrate_kbps !== null)) && (
                            <span className={styles.audioMetaRow}>
                              {track.effective_bpm !== null && (
                                <span
                                  className={cx(
                                    styles.bpmMeta,
                                    track.effective_bpm_source === 'detected' || track.effective_bpm_source === 'corrected'
                                      ? styles.bpmMetaAutoDetected
                                      : ''
                                  )}
                                  title={track.effective_bpm_source === 'number' || track.effective_bpm_source === 'tap' ? 'Manual BPM' : track.effective_bpm_source === 'corrected' ? 'Corrected BPM' : 'Detected BPM'}
                                >
                                  {(track.effective_bpm_source === 'detected' || track.effective_bpm_source === 'corrected') && (
                                    <Bot size={12} aria-hidden="true" />
                                  )}
                                  <span>{formatBpm(track.effective_bpm)}</span>
                                </span>
                              )}
                              {hasPlaybackMp3 && track.playback_bitrate_kbps !== null && (
                                <span
                                  className={styles.mp3BitrateMeta}
                                  title="MP3 playback bitrate"
                                >
                                  {formatPlaybackBitrate(track.playback_bitrate_kbps)}
                                </span>
                              )}
                            </span>
                          )}
                          <span className={styles.mobileGroups} aria-label={`Groups for ${track.title}`}>
                            {track.groups.length > 0
                              ? track.groups.map((group) => group.name).join(', ')
                              : 'No groups'}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className={styles.labelsCell} data-label="Labels">
                      <div className={styles.labelsCellInner}>
                        {track.audio_analysis.status !== 'never_run' && (
                          <span
                            aria-label={getEssentiaAnalysisLabel(track)}
                            className={cx(
                              styles.analysisIndicator,
                              track.audio_analysis.status === 'completed' ? styles.analysisCompleted : '',
                              track.audio_analysis.status === 'failed' ? styles.analysisFailed : '',
                              track.audio_analysis.status === 'queued' ? styles.analysisQueued : '',
                              track.audio_analysis.status === 'processing' ? styles.analysisProcessing : '',
                              track.audio_analysis.pending_suggestion_count > 0 ? styles.analysisPendingSuggestions : ''
                            )}
                            role="img"
                            title={getEssentiaAnalysisLabel(track)}
                          >
                            {track.audio_analysis.status === 'queued' || track.audio_analysis.status === 'processing' ? (
                              <RefreshCw size={13} />
                            ) : track.audio_analysis.status === 'failed' ? (
                              <TriangleAlert size={13} />
                            ) : (
                              <Check size={13} />
                            )}
                          </span>
                        )}
                        <div className={styles.labelsContent}>
                          {track.groups.length > 0 && (
                            <span className={styles.desktopGroups} aria-label={`Groups for ${track.title}`}>
                              {track.groups.map((group) => group.name).join(', ')}
                            </span>
                          )}
                          {trackLabels.length > 0 ? (
                            <span className={labelUiStyles.row} aria-label={`Labels for ${track.title}`}>
                              {trackLabels.slice(0, 6).map((label) => (
                                <span className={labelChipClassName(label.category)} key={`${label.category}-${label.id}`}>
                                  {label.name}
                                </span>
                              ))}
                              {trackLabels.length > 6 && (
                                <span className={labelMoreClassName}>+{trackLabels.length - 6}</span>
                              )}
                              <button
                                aria-label={`Copy labels for ${track.title}`}
                                className={styles.labelCopyButton}
                                onClick={() => void copyTrackLabels(track)}
                                title="Copy labels"
                                type="button"
                              >
                                <Clipboard size={13} />
                              </button>
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="track-secondary-cell" data-label="Duration">{formatDuration(track.duration_seconds) ?? <span className="muted">—</span>}</td>
                    <td className="track-secondary-cell" data-label="Added">{formatAddedDate(track.created_at)}</td>
                    <td className={styles.actionsCell} data-label="Actions" data-track-action-surface>
                      <div
                        className={styles.actionMenuWrap}
                        data-track-action-surface
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <IconButton
                          active={actionMenuOpen}
                          aria-expanded={actionMenuOpen}
                          aria-haspopup={isMobileToolbar ? 'dialog' : 'menu'}
                          icon={<MoreVertical size={15} />}
                          label={`Open actions for ${track.title}`}
                          onClick={(event) => toggleTrackActionMenu(event, track.id)}
                          title="Actions"
                        />
                        {actionMenuOpen && !isMobileToolbar && (
                          <div
                            className={cx(styles.actionMenu, trackActionMenuPosition?.placement === 'above' && styles.opensAbove)}
                            data-track-action-surface
                            role="menu"
                            style={trackActionMenuPosition ? {
                              left: trackActionMenuPosition.left,
                              top: trackActionMenuPosition.top
                            } : undefined}
                          >
                            <button
                              className={styles.actionMenuItem}
                              onClick={() => {
                                closeTrackActionMenu()
                                props.onEdit(track)
                              }}
                              role="menuitem"
                              type="button"
                              aria-label={`Edit ${track.title}`}
                            >
                              <Pencil size={15} />
                              <span>Edit</span>
                            </button>
                            <button
                              className={styles.actionMenuItem}
                              onClick={() => {
                                closeTrackActionMenu()
                                props.onManageGroups(track)
                              }}
                              role="menuitem"
                              type="button"
                              aria-label={`Manage groups for ${track.title}`}
                            >
                              <FolderOpen size={15} />
                              <span>Manage groups</span>
                            </button>
                            <button
                              className={styles.actionMenuItem}
                              onClick={() => {
                                closeTrackActionMenu()
                                void props.onReprocess(track)
                              }}
                              role="menuitem"
                              type="button"
                              aria-label={`Reprocess ${track.title}`}
                            >
                              <RefreshCw size={15} />
                              <span>Reprocess</span>
                            </button>
                            <button
                              className={cx(styles.actionMenuItem, styles.actionDanger)}
                              onClick={() => {
                                closeTrackActionMenu()
                                void props.onDelete(track)
                              }}
                              role="menuitem"
                              type="button"
                              aria-label={`Remove ${track.title}`}
                            >
                              <Trash2 size={15} />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                        {actionMenuOpen && isMobileToolbar && (
                          <div
                            className={styles.actionSheetBackdrop}
                            onMouseDown={(event) => {
                              if (event.target === event.currentTarget) {
                                closeTrackActionMenu()
                              }
                            }}
                          >
                            <section
                              aria-label={`Actions for ${track.title}`}
                              aria-modal="true"
                              className={styles.actionSheetPanel}
                              data-track-action-surface
                              onMouseDown={(event) => event.stopPropagation()}
                              role="dialog"
                            >
                              <div className={styles.actionSheetHeader}>
                                <span>Actions</span>
                                <strong>{trackDisplayTitle}</strong>
                              </div>
                              <div className={styles.actionSheetActions}>
                                <button
                                  className={styles.actionMenuItem}
                                  onClick={() => {
                                    closeTrackActionMenu()
                                    props.onEdit(track)
                                  }}
                                  type="button"
                                  aria-label={`Edit ${track.title}`}
                                >
                                  <Pencil size={17} />
                                  <span>Edit</span>
                                </button>
                                <button
                                  className={styles.actionMenuItem}
                                  onClick={() => {
                                    closeTrackActionMenu()
                                    props.onManageGroups(track)
                                  }}
                                  type="button"
                                  aria-label={`Manage groups for ${track.title}`}
                                >
                                  <FolderOpen size={17} />
                                  <span>Manage groups</span>
                                </button>
                                <button
                                  className={styles.actionMenuItem}
                                  onClick={() => {
                                    closeTrackActionMenu()
                                    void props.onReprocess(track)
                                  }}
                                  type="button"
                                  aria-label={`Reprocess ${track.title}`}
                                >
                                  <RefreshCw size={17} />
                                  <span>Reprocess</span>
                                </button>
                                <button
                                  className={cx(styles.actionMenuItem, styles.actionDanger)}
                                  onClick={() => {
                                    closeTrackActionMenu()
                                    void props.onDelete(track)
                                  }}
                                  type="button"
                                  aria-label={`Remove ${track.title}`}
                                >
                                  <Trash2 size={17} />
                                  <span>Delete</span>
                                </button>
                              </div>
                              <button
                                className={styles.actionSheetCancel}
                                onClick={closeTrackActionMenu}
                                type="button"
                              >
                                Cancel
                              </button>
                            </section>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            </DataTableElement>
            {props.items.length === 0 && (
              <EmptyState
                message={props.loading
                  ? 'Loading audio tracks...'
                  : activeFilterCount > 0 || props.filters.search
                    ? 'No audio tracks match these filters.'
                    : 'No audio tracks yet.'}
              />
            )}
          </div>
          {pagination}
        </DataTableSurface>
        <div className={styles.mobilePagination}>
          {pagination}
        </div>
      </div>

      {labelFilterDialogOpen && (
        <LabelBrowser
          actions={(
            <>
              <Button disabled={!canClearFilterDialog} onClick={clearLabelFilter}>
                Clear
              </Button>
              <Button onClick={applyLabelFilter} variant="primary">
                Apply{draftFilterCount > 0 ? ` ${draftFilterCount}` : ''}
              </Button>
            </>
          )}
          ariaLabel={filterDialogTitle}
          body={(
            <div className={cx(isMobileToolbar ? 'mobile-filter-tab-panel' : 'desktop-filter-tab-panel')}>
              {!isMobileToolbar && renderLabelFilterList()}
              {isMobileToolbar && mobileFilterTab === 'groups' && (
                <div className={cx('audio-filter-group-list')} aria-label="Audio groups">
                  <button
                    className={cx('audio-filter-group-chip', !groupFilterDraftId && 'selected')}
                    onClick={() => setGroupFilterDraftId(undefined)}
                    type="button"
                  >
                    All groups
                  </button>
                  {props.groups.map((group) => {
                    const selected = groupFilterDraftId === group.id

                    return (
                      <button
                        className={cx('audio-filter-group-chip', selected && 'selected')}
                        key={group.id}
                        onClick={() => setGroupFilterDraftId(group.id)}
                        type="button"
                      >
                        {group.name}
                      </button>
                    )
                  })}
                  {props.groups.length === 0 && <span className="muted">No audio groups created.</span>}
                </div>
              )}
              {isMobileToolbar && mobileFilterTab === 'prompts' && (
                <div className={cx('audio-filter-group-list')} aria-label="Prompt Library links">
                  <button
                    className={cx('audio-filter-group-chip', !sunoPromptBindingDraft && 'selected')}
                    onClick={() => setSunoPromptBindingDraft(undefined)}
                    type="button"
                  >
                    All prompts
                  </button>
                  <button
                    className={cx('audio-filter-group-chip', sunoPromptBindingDraft === 'linked' && 'selected')}
                    onClick={() => setSunoPromptBindingDraft('linked')}
                    type="button"
                  >
                    Linked
                  </button>
                  <button
                    className={cx('audio-filter-group-chip', sunoPromptBindingDraft === 'unlinked' && 'selected')}
                    onClick={() => setSunoPromptBindingDraft('unlinked')}
                    type="button"
                  >
                    Unlinked
                  </button>
                </div>
              )}
              {isMobileToolbar && mobileFilterTab === 'flac' && (
                <div className={cx('audio-filter-group-list')} aria-label="FLAC availability">
                  <button
                    className={cx('audio-filter-group-chip', !losslessDraft && 'selected')}
                    onClick={() => setLosslessDraft(undefined)}
                    type="button"
                  >
                    All FLAC
                  </button>
                  {(Object.entries(losslessFilterLabels) as Array<[NonNullable<AudioTrackFilters['lossless']>, string]>).map(([value, label]) => (
                    <button
                      className={cx('audio-filter-group-chip', losslessDraft === value && 'selected')}
                      key={value}
                      onClick={() => setLosslessDraft(value)}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {isMobileToolbar && mobileFilterTab === 'labels' && renderLabelFilterSelection()}
            </div>
          )}
          className="audio-label-filter-panel"
          closeLabel={isMobileToolbar ? 'Close filters' : 'Close label filter'}
          description={!isMobileToolbar ? filterDialogDescription : null}
          onClose={closeLabelFilterDialog}
          panelRef={labelFilterPanelRef}
          title={filterDialogTitle}
          tools={(
            <>
              {isMobileToolbar ? (
                <div className={cx('mobile-filter-tabs')} role="tablist" aria-label="Track filter sections">
                  {mobileFilterTabs.map((tab) => {
                    const count = getMobileFilterTabCount(tab.id)

                    return (
                      <button
                        aria-selected={mobileFilterTab === tab.id}
                        className={cx('mobile-filter-tab', mobileFilterTab === tab.id && 'active')}
                        key={tab.id}
                        onClick={() => setMobileFilterTab(tab.id)}
                        role="tab"
                        type="button"
                      >
                        <span>{tab.label}</span>
                        {count > 0 && <strong>{count}</strong>}
                      </button>
                    )
                  })}
                </div>
              ) : renderLabelFilterControls()}
            </>
          )}
        />
      )}

      {upload.isOpen && (
        <AudioUploadDialog
          {...upload.dialogProps}
          groups={props.groups}
          uploading={props.uploading}
        />
      )}
    </>
  )
}
